import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import GridCard from '@Guidelines/Component/GridCard';
import SubjectTypeTile from '@Guidelines/Component/SubjectTypeTile';
import { theme } from '@Guidelines/Theme/theme';
import describeAbout from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard/Helpers/describeAbout';
import describeAccess from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard/Helpers/describeAccess';
import describeFieldCount from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard/Helpers/describeFieldCount';
import SubjectTypeCardActions from '@UI/Pages/Administration/SubjectTypes/SubjectTypeList/SubjectTypeCard/SubjectTypeCardActions';
import type SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag } from '@verclam/ui-kit';

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const accessStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

const noAccessStyle = css`
	${accessStyle}
	color: ${theme.states.warning.ink};
`;

type Props = {
	readonly subjectType: SubjectTypeDefinitionView;
};

/** One dossier type: its name, what it holds and covers, who may use it, and its actions. */
const SubjectTypeCard: React.FC<Props> = ({ subjectType }) => {
	const hasNoAccess = subjectType.usableBy.length === 0;

	return (
		<GridCard layout="stacked" rowGap="0.75rem">
			<div css={titleRowStyle}>
				<SubjectTypeTile icon={subjectType.icon.value} color={subjectType.color.value} />
				<h2 css={titleStyle}>{subjectType.wording.plural}</h2>
				<ConditionalLoader data={subjectType.isArchived}>
					<Tag value="Archivé" severity="secondary" rounded />
				</ConditionalLoader>
			</div>
			<Eyebrow>
				{describeFieldCount(subjectType.fields)} · {describeAbout(subjectType.about)}
			</Eyebrow>
			<p css={hasNoAccess ? noAccessStyle : accessStyle}>{describeAccess(subjectType.usableBy)}</p>
			<SubjectTypeCardActions subjectType={subjectType} />
		</GridCard>
	);
};

export default SubjectTypeCard;
